/**
 * Template Icon Component — shared icon mapping for MCP templates.
 *
 * Maps Lucide icon name strings (stored in template.icon) to React components.
 * Used by: MCPCatalogPage, SystemTemplatesPage, MCPServersListPage, forms.
 *
 * Sprint: 41 (fix)
 */

import * as React from 'react';
import {
  Server,
  Ticket,
  Activity,
  MessageSquare,
  Terminal,
  Cpu,
  BellRing,
  Users,
  Wrench,
  Shield,
  Globe,
  Database,
  Mail,
  FileText,
  Settings,
  Zap,
  Radio,
  Cloud,
  Lock,
  Eye,
  BarChart3,
  GitBranch,
  Webhook,
  MonitorSpeaker,
  Code,
  Boxes,
  Container,
  Network,
  Bot,
  Sparkles,
  CircuitBoard,
  HardDrive,
  Upload,
  X,
  ImageIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// Icon Registry — single source of truth
// =============================================================================

export const TEMPLATE_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  // Infrastructure & servers
  server: Server,
  terminal: Terminal,
  cpu: Cpu,
  'hard-drive': HardDrive,
  database: Database,
  cloud: Cloud,
  network: Network,
  container: Container,
  boxes: Boxes,
  'circuit-board': CircuitBoard,

  // ITSM & project management
  ticket: Ticket,
  'kanban-square': Boxes,

  // Communication
  'message-square': MessageSquare,
  mail: Mail,
  users: Users,

  // Monitoring & alerting
  activity: Activity,
  'bell-ring': BellRing,
  'bar-chart-3': BarChart3,
  'monitor-speaker': MonitorSpeaker,
  radio: Radio,

  // Security & auth
  shield: Shield,
  lock: Lock,
  eye: Eye,

  // Development
  code: Code,
  'git-branch': GitBranch,
  webhook: Webhook,

  // AI & automation
  bot: Bot,
  sparkles: Sparkles,
  zap: Zap,

  // Documents & config
  'file-text': FileText,
  settings: Settings,
  globe: Globe,
  wrench: Wrench,
};

/** All available icon names for the picker */
export const TEMPLATE_ICON_NAMES = Object.keys(TEMPLATE_ICON_MAP);

/** Default fallback icon */
export const DEFAULT_TEMPLATE_ICON = Wrench;

/**
 * Resolve a template icon name to a Lucide component.
 */
export function getTemplateIconComponent(
  iconName: string | null | undefined
): React.ComponentType<{ className?: string }> {
  if (!iconName) return DEFAULT_TEMPLATE_ICON;
  return TEMPLATE_ICON_MAP[iconName] ?? DEFAULT_TEMPLATE_ICON;
}

// =============================================================================
// TemplateIcon component — renders the icon with optional styling
// =============================================================================

interface TemplateIconProps {
  icon: string | null | undefined;
  /** Whether a custom uploaded icon exists for this template */
  hasCustomIcon?: boolean;
  /** Template slug — needed to build the custom icon URL */
  slug?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-8 h-8',
};

export function TemplateIcon({ icon, hasCustomIcon, slug, size = 'md', className }: TemplateIconProps) {
  const [imgError, setImgError] = React.useState(false);

  // If custom icon exists + we have slug + image didn't fail to load → show <img>
  if (hasCustomIcon && slug && !imgError) {
    return (
      <img
        src={`/api/v1/mcp-templates/${slug}/icon`}
        alt=""
        onError={() => setImgError(true)}
        className={cn(sizeClasses[size], 'object-contain', className)}
      />
    );
  }

  // Fallback: Lucide icon
  const IconComponent = getTemplateIconComponent(icon);
  return <IconComponent className={cn(sizeClasses[size], className)} />;
}

// =============================================================================
// IconPicker — visual grid picker for template forms
// =============================================================================

interface IconPickerProps {
  value: string | undefined;
  onChange: (iconName: string) => void;
  className?: string;
}

export function IconPicker({ value, onChange, className }: IconPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const filteredIcons = search
    ? TEMPLATE_ICON_NAMES.filter((name) => name.includes(search.toLowerCase()))
    : TEMPLATE_ICON_NAMES;

  const SelectedIcon = getTemplateIconComponent(value);

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {/* Trigger button showing current selection */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 border rounded-md w-full text-left',
          'hover:bg-muted/50 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-ring',
          open && 'ring-2 ring-ring'
        )}
      >
        <SelectedIcon className="w-5 h-5 text-muted-foreground" />
        <span className="text-sm flex-1 truncate">
          {value || 'wrench'}
        </span>
        <span className="text-xs text-muted-foreground">▾</span>
      </button>

      {/* Dropdown picker */}
      {open && (
        <div className="absolute left-0 top-full mt-1 w-80 bg-popover border rounded-md shadow-lg z-50">
          {/* Search */}
          <div className="p-2 border-b">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search icons..."
              className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-ring bg-background"
              autoFocus
            />
          </div>

          {/* Icon grid */}
          <div className="p-2 max-h-48 overflow-y-auto">
            <div className="grid grid-cols-8 gap-1">
              {filteredIcons.map((name) => {
                const Ic = TEMPLATE_ICON_MAP[name];
                const isSelected = name === value;
                return (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    onClick={() => {
                      onChange(name);
                      setOpen(false);
                      setSearch('');
                    }}
                    className={cn(
                      'p-2 rounded hover:bg-muted transition-colors flex items-center justify-center',
                      isSelected && 'bg-primary/10 ring-1 ring-primary'
                    )}
                  >
                    <Ic className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
            {filteredIcons.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">
                No icons match "{search}"
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// CustomIconUpload — upload / preview / remove a custom vendor icon
// =============================================================================

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
const MAX_SIZE_KB = 256;

interface CustomIconUploadProps {
  slug?: string;
  hasCustomIcon: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
  isPending?: boolean;
  className?: string;
}

export function CustomIconUpload({
  slug,
  hasCustomIcon,
  onUpload,
  onRemove,
  isPending,
  className,
}: CustomIconUploadProps) {
  const [preview, setPreview] = React.useState<string | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const validateAndSet = (file: File) => {
    setError(null);
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Invalid type. Allowed: PNG, JPG, WebP, SVG');
      return;
    }
    if (file.size > MAX_SIZE_KB * 1024) {
      setError(`File too large. Max ${MAX_SIZE_KB}KB`);
      return;
    }
    // Use data: URL (CSP-safe) instead of blob: URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    onUpload(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndSet(file);
  };

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = '';
    onRemove();
  };

  const iconUrl = hasCustomIcon && slug
    ? `/api/v1/mcp-templates/${slug}/icon?_t=${Date.now()}`
    : null;

  return (
    <div className={cn('space-y-2', className)}>
      <label className="text-sm font-medium">Custom Icon</label>
      <p className="text-xs text-muted-foreground">
        Upload a vendor logo (PNG, JPG, WebP, SVG — max 256 KB). Overrides the Lucide icon.
      </p>

      {(preview || iconUrl) && (
        <div className="flex items-center gap-3 p-3 border rounded-md bg-muted/30">
          <img
            src={preview || iconUrl!}
            alt="Custom icon"
            className="w-10 h-10 object-contain rounded"
          />
          <div className="flex-1 text-sm text-muted-foreground">
            {preview ? 'New icon (pending save)' : 'Current custom icon'}
          </div>
          <button
            type="button"
            onClick={handleRemove}
            disabled={isPending}
            className="p-1 rounded hover:bg-destructive/10 text-destructive transition-colors"
            title="Remove custom icon"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {!preview && !iconUrl && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className={cn(
            'flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
            dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-muted-foreground/50',
            isPending && 'opacity-50 pointer-events-none',
          )}
        >
          {isPending ? (
            <Upload className="w-6 h-6 text-muted-foreground animate-pulse mb-2" />
          ) : (
            <ImageIcon className="w-6 h-6 text-muted-foreground mb-2" />
          )}
          <span className="text-sm text-muted-foreground">
            {isPending ? 'Uploading…' : 'Drop image here or click to browse'}
          </span>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.svg"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) validateAndSet(file);
        }}
      />

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
